import type { SharingDecision, SharingRequest, SharingRequestWithdrawal } from '@ralph/contracts'
import { apiGet, apiSend } from './client.ts'

/**
 * Sharing requests (PRD-26 D-29): one queue for agents and knowledge bases.
 *
 * A request exists when somebody who is not an administrator widens what is live to everyone, or
 * to a group they do not manage (D-13); everything else went live when they asked (D-40). The
 * `digest` on each request is computed when it is read, over what the reader sees (D-49) — a
 * decision sends it back, and the server refuses it if anything on the card changed since.
 */

const requestPath = (id: string): string => `/api/sharing-requests/${encodeURIComponent(id)}`

/** The signed-in person's own requests: what waits, and how the rest ended. */
export const fetchMySharingRequests = (): Promise<{ items: SharingRequest[] }> =>
  apiGet<{ items: SharingRequest[] }>('/api/sharing-requests/mine')

/** One request — for the item's current maintainers (whoever asked among them) and administrators. */
export const fetchSharingRequest = (id: string): Promise<SharingRequest> => apiGet<SharingRequest>(requestPath(id))

/** Withdraws it; the answer is what the withdrawal did, not the card. */
export const withdrawSharingRequest = (id: string): Promise<SharingRequestWithdrawal> =>
  apiSend<SharingRequestWithdrawal>(`${requestPath(id)}/withdraw`, 'POST')

/** The administrators' queue: pending, oldest first — or the recent history of all of them. */
export const fetchSharingQueue = (status: 'pending' | 'all' = 'pending'): Promise<{ items: SharingRequest[] }> =>
  apiGet<{ items: SharingRequest[] }>(`/api/admin/sharing-requests?status=${status}`)

/** Approve or decline, whole (D-15). A decline carries a note; both carry the digest read. */
export const decideSharingRequest = (id: string, decision: SharingDecision): Promise<SharingRequest> =>
  apiSend<SharingRequest>(`/api/admin/sharing-requests/${encodeURIComponent(id)}/decide`, 'POST', decision)
